import React from 'react';
import { MessageCircle, Eye, Edit3 } from 'lucide-react';
import type { UnifiedLead } from './types';
import { STATUS_COLORS, DEFAULT_STATUS_COLOR } from './types';

interface LeadRowProps {
  lead: UnifiedLead;
  onStatusChange: (lead: UnifiedLead) => void;
  onViewDetails: () => void;
  isSelected?: boolean;
  onToggleSelect?: (id: number) => void;
}

export default function LeadRow({ lead, onStatusChange, onViewDetails, isSelected, onToggleSelect }: LeadRowProps) {
  const statusColor = STATUS_COLORS[lead.status] || DEFAULT_STATUS_COLOR;
  const date = new Date(lead.created_at);
  const formattedDate = date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' });
  const formattedTime = date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });

  const cleanPhone = lead.phone?.replace(/[^0-9+]/g, '') || '';
  const whatsappLink = cleanPhone ? `https://wa.me/${cleanPhone.replace('+', '')}` : '';

  return (
    <tr className="group hover:bg-indigo-50/30 transition-colors">
      {/* Checkbox */}
      <td className="px-4 py-3.5 w-10">
        <input
          type="checkbox"
          checked={!!isSelected}
          onChange={() => onToggleSelect?.(lead.id)}
          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
        />
      </td>
      {/* Date */}
      <td className="px-4 py-3.5 whitespace-nowrap">
        <div className="text-sm font-medium text-slate-700 tabular-nums">{formattedDate}</div>
        <div className="text-[11px] text-slate-400 tabular-nums">{formattedTime}</div>
      </td>

      {/* Name */}
      <td className="px-4 py-3.5 max-w-[180px]">
        <button onClick={onViewDetails} className="text-sm font-semibold text-slate-900 hover:text-indigo-600 transition-colors text-left break-all">
          {lead.name}
        </button>
        {lead.country && <div className="text-[11px] text-slate-400 mt-0.5">{lead.country}</div>}
        <div className="flex flex-wrap gap-1 mt-1">
          {lead.delivery_channel && lead.delivery_channel !== 'web' && (
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
                lead.delivery_channel === 'beacon'
                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                  : lead.delivery_channel === 'retry'
                  ? 'bg-sky-50 text-sky-700 border border-sky-200'
                  : 'bg-slate-50 text-slate-600 border border-slate-200'
              }`}
              title={
                lead.delivery_channel === 'beacon'
                  ? 'Delivered via sendBeacon fallback (network failure)'
                  : lead.delivery_channel === 'retry'
                  ? 'Auto-replayed from browser localStorage'
                  : `Delivery channel: ${lead.delivery_channel}`
              }
            >
              {lead.delivery_channel}
            </span>
          )}
          {lead.spam_flags && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-200"
              title={`Anti-spam flags: ${lead.spam_flags}`}
            >
              flagged
            </span>
          )}
        </div>
      </td>

      {/* Phone */}
      <td className="px-4 py-3.5 hidden md:table-cell">
        <div className="flex items-center gap-2">
          <a href={`tel:${cleanPhone}`} className="text-sm text-slate-700 hover:text-indigo-600 transition-colors tabular-nums">
            {lead.phone || '—'}
          </a>
          {whatsappLink && (
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-600 flex-shrink-0" title="WhatsApp">
              <MessageCircle className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </td>

      {/* Dial Code */}
      <td className="px-4 py-3.5 hidden md:table-cell">
        {lead.country_code ? (
          <span className="text-[11px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-lg">{lead.country_code}</span>
        ) : (
          <span className="text-xs text-slate-300">—</span>
        )}
      </td>

      {/* Email */}
      <td className="px-4 py-3.5 hidden lg:table-cell">
        <a href={`mailto:${lead.email}`} className="text-sm text-slate-600 hover:text-indigo-600 transition-colors truncate block max-w-[180px]">
          {lead.email}
        </a>
      </td>

      {/* Message */}
      <td className="px-4 py-3.5 hidden xl:table-cell">
        {lead.message ? (
          <span className="text-xs text-slate-500 block max-w-[160px] truncate" title={lead.message}>{lead.message}</span>
        ) : (
          <span className="text-xs text-slate-300">—</span>
        )}
      </td>

      {/* Type */}
      <td className="px-4 py-3.5">
        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-bold uppercase tracking-wider ${
          lead.lead_type === 'contact'
            ? 'bg-blue-50 text-blue-600'
            : 'bg-purple-50 text-purple-600'
        }`}>
          {lead.lead_type === 'contact' ? 'Contact' : 'Eligibility'}
        </span>
      </td>

      {/* Score */}
      <td className="px-4 py-3.5 hidden lg:table-cell">
        {lead.score != null ? (
          <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl text-sm font-bold ${
            lead.score >= 65 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'
          }`}>
            {lead.score}
          </span>
        ) : (
          <span className="text-xs text-slate-300">—</span>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-3.5">
        <button
          onClick={() => onStatusChange(lead)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-all hover:ring-2 hover:ring-offset-1 hover:ring-current/20 ${statusColor.bg} ${statusColor.text}`}
        >
          <span className={`w-2 h-2 rounded-full ${statusColor.dot}`} />
          {lead.status}
          <Edit3 className="w-3 h-3 ml-0.5 opacity-0 group-hover:opacity-60 transition-opacity" />
        </button>
      </td>

      {/* Remark */}
      <td className="px-4 py-3.5 hidden lg:table-cell">
        {lead.remark ? (
          <div className="max-w-[160px]">
            <span className="text-xs text-slate-600 block truncate" title={lead.remark}>{lead.remark}</span>
            {lead.called_date && (
              <span className="text-[10px] text-indigo-400 font-medium mt-0.5 block">
                Called {new Date(lead.called_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}
              </span>
            )}
          </div>
        ) : lead.called_date ? (
          <span className="text-[10px] text-indigo-400 font-medium">
            Called {new Date(lead.called_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}
          </span>
        ) : (
          <span className="text-xs text-slate-300">—</span>
        )}
      </td>

      {/* Actions */}
      <td className="px-4 py-3.5">
        <button
          onClick={onViewDetails}
          className="p-2 rounded-xl text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all"
          title="View Details"
        >
          <Eye className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}
